import 'leaflet/dist/leaflet.css';

import classNames from 'classnames';
import React, { useCallback, useRef } from 'react';
import { MapContainer, ImageOverlay, Pane, TileLayer } from 'react-leaflet';

import { LAYER_PARAMS } from '~/helpers/supply';
import useMount from '~/hooks/useMount';
import { useAppDispatch, useAppSelector } from '~/store';

import { supplyMapSelector } from '~/store/selectors/supplySelectors';
import { playClanBaseAmbient, stopClanBaseAmbient } from '~/web2ClientAPI/sounds';
import { actionsSupply, selectBuildingThunk, setSelectedBuildingThunk } from '~/Actions/ActionSupply';

import { maxBounds } from '~/Reducers/ReducerSupply';

import styles from './SupplyMap.module.scss';
import BuildingsLayer from './SupplyMapLayers/BuildingsLayer';
import MapController from './SupplyMapLayers/MapControllerLayer';
import PreviewLayer from './SupplyMapLayers/PreviewLayer';
import imageBG from './images/bg.jpg';

import type L from 'leaflet';
import type { BUILDINGS } from '~/Actions/ActionSupply';

const ViewSupply: React.FC<{ clanId: number }> = ({ clanId }) => {
  const {
    battlesStats,
    backgroundUrl,
    buildings,
    confirmBuildingId,
    edgesVisible,
    highlightDelay,
    initialZoom,
    isMapBlured,
    isSidebarOpened,
    maxZoom,
    minZoom,
    position,
    prevPosition,
    selectedBuildingId,
    tempHoverBuildingId,
    tempHoverBuildingLevel,
    zoom,
  } = useAppSelector(supplyMapSelector(clanId));

  const dispatch = useAppDispatch();

  const mapRef = useRef<L.Map>(null);

  const setHighlightedBuilding = useCallback(
    (buildingId: Nullable<BUILDINGS>) => {
      dispatch(actionsSupply.setHighlightedBuilding(buildingId));
    },
    [dispatch],
  );

  const dropSelection = useCallback(() => {
    dispatch(setSelectedBuildingThunk(null));
    dispatch(actionsSupply.hideBuildingConfirm());
  }, [dispatch]);

  const selectBuilding = useCallback(
    (buildingId: BUILDINGS, needSavePosition: boolean = false) => {
      dispatch(selectBuildingThunk(buildingId, clanId));

      if (needSavePosition && mapRef.current) {
        dispatch(actionsSupply.saveMapPosition(mapRef.current.getBounds()));
      }
    },
    [dispatch, clanId],
  );

  useMount(() => {
    playClanBaseAmbient();

    const searchParams = new URLSearchParams(window.location.search);
    const buildingId = searchParams.get('buildingId');
    if (buildingId) {
      dispatch(actionsSupply.openSupplySidebar());
      dispatch(setSelectedBuildingThunk(buildingId as BUILDINGS));
    }

    return () => {
      stopClanBaseAmbient();
      window.tooltipProvider?.hide();
    };
  });

  return (
    <MapContainer
      className={classNames(styles.viewMap, { [styles.viewMapOpened]: isSidebarOpened })}
      ref={mapRef}
      bounceAtZoomLimits={false}
      center={position}
      doubleClickZoom={false}
      inertia={false}
      maxBounds={maxBounds}
      maxBoundsViscosity={1.0}
      maxZoom={maxZoom}
      minZoom={minZoom}
      scrollWheelZoom
      touchZoom={false}
      trackResize
      zoom={initialZoom}
      zoomSnap={1}
      zoomDelta={1}
    >
      {/* SVG Filters for CSS use */}
      <div className={styles.filter}>
        <svg height="0" width="0">
          <defs>
            <filter id="blur-filter" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="15" />
            </filter>
            <linearGradient id="linear-gradient" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(0, 255, 255, 0)" />
              <stop offset="50%" stopColor="rgba(0, 255, 240, 0.16)" />
              <stop offset="100%" stopColor="rgba(15, 25, 34, 0.2)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <MapController
        selectedBuildingId={selectedBuildingId}
        tempHoverBuildingId={tempHoverBuildingId}
        prevPosition={prevPosition}
        buildings={buildings}
        dropSelection={dropSelection}
      />

      <Pane name="fallback" style={{ zIndex: -2 }}>
        <ImageOverlay className={styles.overlay} url={imageBG} bounds={maxBounds} opacity={isMapBlured ? 0 : 1} />
      </Pane>

      {backgroundUrl && (
        <TileLayer
          url={backgroundUrl}
          className={styles.blurable}
          {...LAYER_PARAMS}
          opacity={isMapBlured ? 0.2 : 1}
          zIndex={-1}
        />
      )}

      {buildings && (
        <>
          <BuildingsLayer
            battlesStats={battlesStats}
            buildings={buildings}
            edgesVisible={edgesVisible}
            highlightDelay={highlightDelay}
            isMapBlured={isMapBlured}
            selectedBuildingId={selectedBuildingId}
            confirmBuildingId={confirmBuildingId}
            tempHoverBuildingLevel={tempHoverBuildingLevel?.level || null}
            zoom={zoom}
            setHighlightedBuilding={setHighlightedBuilding}
            selectBuilding={selectBuilding}
          />
          <PreviewLayer
            buildings={buildings}
            confirmBuildingId={confirmBuildingId}
            tempHoverBuildingId={tempHoverBuildingId}
            tempHoverBuildingLevel={tempHoverBuildingLevel}
          />
        </>
      )}
    </MapContainer>
  );
};

export default ViewSupply;
